import { useEffect, useState } from 'react';
import { Tag, Space, Button, Card, message, Modal, Row, Col, Select, Typography } from 'antd'; 
import { EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import { getAllOrders, deleteOrder, batchDeleteOrders, getOrderDetail, updateAdminOrderRechargeResult } from '../../api';
import ListToolbar from '../../components/ListToolbar';
import { exportToCSV } from '../../utils/exportUtils';
import PageTitle from '../../components/PageTitle';
import ResizableTable from '../../components/ResizableTable';

const OrderList = () => {
  const [orderData, setOrderData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isBatchDeleteModalVisible, setIsBatchDeleteModalVisible] = useState(false);
  const [isDetailModalVisible, setIsDetailModalVisible] = useState(false);
  const [currentDeleteOrderId, setCurrentDeleteOrderId] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [orderDetail, setOrderDetail] = useState(null);
  /** 管理端测试：手动改充值到账状态 */
  const [rechargeStatusModal, setRechargeStatusModal] = useState({
    open: false,
    order: null,
    status: 0,
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [displayColumns, setDisplayColumns] = useState([]);

  /** 服务端分页：每页条数由后端限制（最大 100），与 GetAllOrders 一致 */
  const fetchOrders = async (search, page, pageSize) => {
    setLoading(true);
    try {
      const res = await getAllOrders(search, page, pageSize);
      if (res?.success && Array.isArray(res.data)) {
        setOrderData(res.data);
        setPagination((prev) => ({
          ...prev,
          current: res.page ?? page,
          pageSize: res.page_size ?? pageSize,
          total: Number(res.total) || 0,
        }));
      } else {
        setOrderData([]);
        setPagination((prev) => ({ ...prev, current: 1, total: 0 }));
        message.error('订单数据格式错误');
      }
    } catch (err) {
      console.error('加载订单失败：', err);
      message.error('加载订单失败，请检查接口连接');
      setOrderData([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
    } finally {
      setLoading(false);
    }
  };

  // 首屏只拉第 1 页（按需加载后续页）
  useEffect(() => {
    fetchOrders('', 1, 20);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 仅挂载时拉首屏
  }, []);

  const handleSearch = () => {
    setSelectedRowKeys([]);
    fetchOrders(searchText.trim(), 1, pagination.pageSize);
  };

  const handleReset = () => {
    setSearchText('');
    setSelectedRowKeys([]);
    fetchOrders('', 1, pagination.pageSize);
  };

  const handleRefresh = () => {
    fetchOrders(searchText.trim(), pagination.current, pagination.pageSize);
  };

  // 导出数据
  const handleExport = () => {
    const exportData = orderData.map(order => ({
      '订单ID': order.id,
      '订单号': order.order_no || '-',
      '用户ID': order.user_id,
      '租客手机号': order.user_phone || '-',
      '房源名称': order.house_name || '-',
      '房间号': order.room_fang_hao || '-',
      '房间ID': order.room_id ?? '-',
      '订单金额': `¥${Number(order.order_amount || 0).toFixed(2)}`,
      '订单类型': (() => {
        const typeMap = {
          1: '房租订单',
          2: '电表电费订单',
          3: '水表水费订单',
        };
        return typeMap[order.order_type] || '未知';
      })(),
      '电表服务费': (() => {
        if (order.order_type === 2) {
          const orderAmount = Number(order.order_amount || 0);
          const landlordFee = Number(order.landlord_fee || 0);
          const serviceFee = orderAmount - landlordFee;
          const finalAmount = isNaN(serviceFee) ? 0 : serviceFee;
          return `¥${finalAmount.toFixed(2)}`;
        }
        return '¥0.00';
      })(),
      '水表服务费': (() => {
        if (order.order_type === 3) {
          const orderAmount = Number(order.order_amount || 0);
          const landlordFee = Number(order.landlord_fee || 0);
          const serviceFee = orderAmount - landlordFee;
          const finalAmount = isNaN(serviceFee) ? 0 : serviceFee;
          return `¥${finalAmount.toFixed(2)}`;
        }
        return '¥0.00';
      })(),
      '租房服务费': (() => {
        if (order.order_type === 1) {
          const orderAmount = Number(order.order_amount || 0);
          const landlordFee = Number(order.landlord_fee || 0);
          const serviceFee = orderAmount - landlordFee;
          const finalAmount = isNaN(serviceFee) ? 0 : serviceFee;
          return `¥${finalAmount.toFixed(2)}`;
        }
        return '¥0.00';
      })(),
      '充值度数': order.recharge_power ? `${Number(order.recharge_power).toFixed(2)}度` : '-',
      '购电次数': (() => {
        if (Number(order.order_type) !== 2) return '-';
        const v = order.meter_purchase_count ?? order.meterPurchaseCount;
        if (v === null || v === undefined || v === '') return '-';
        const n = Number(v);
        return Number.isFinite(n) ? String(Math.floor(n)) : '-';
      })(),
      '订单状态': (!order.order_status_text || order.order_status_text === '未知状态' || order.order_status_text === '未知') ? '支付取消' : order.order_status_text,
      // 导出时也按规则处理：房租订单充值状态为空
      '充值状态': order.order_type === 1 ? '-' : (order.recharge_status_text || '未知'),
      '支付时间': order.pay_time || '-',
      '房东分账金额': `¥${Number(order.landlord_fee || 0).toFixed(2)}`,
      '公司分账金额': `¥${Number(order.company_fee || 0).toFixed(2)}`,
      '分账人A金额': `¥${Number(order.shareholder_a_fee || 0).toFixed(2)}`,
      '分账人B金额': `¥${Number(order.shareholder_b_fee || 0).toFixed(2)}`,
      '分账人C金额': `¥${Number(order.shareholder_c_fee || 0).toFixed(2)}`,
      '银行手续费': `¥${Number(order.bank_fee || 0).toFixed(2)}`,
      '创建时间': order.create_time,
    }));

    if (exportData.length === 0) {
      message.warning('当前页没有可导出的订单');
      return;
    }
    if (exportToCSV(exportData, '订单列表')) {
      message.success(`已导出当前页 ${exportData.length} 条（全量导出需后续单独接口）`);
    } else {
      message.error('导出数据失败，请重试');
    }
  };

  const handleTableChange = (newPagination) => {
    setSelectedRowKeys([]);
    fetchOrders(
      searchText.trim(),
      newPagination.current,
      newPagination.pageSize
    );
  };

  // 打开单个删除弹窗
  const showDeleteModal = (orderId) => {
    console.log('===== 触发删除订单弹窗 =====');
    console.log('待删除订单ID：', orderId, '类型：', typeof orderId);
    
    if (!orderId) {
      console.error('订单ID为空！');
      message.warning('订单ID不能为空，请刷新页面重试');
      return;
    }
    if (isNaN(Number(orderId))) {
      console.error('订单ID格式错误，不是数字：', orderId);
      message.warning('订单ID必须为数字，请检查数据');
      return;
    }

    setCurrentDeleteOrderId(orderId);
    setIsDeleteModalVisible(true);
  };

  // 确认单个删除
  const handleDeleteConfirm = async () => {
    const finalOrderId = String(currentDeleteOrderId).trim();
    if (!finalOrderId) {
      console.error('当前要删除的订单ID为空！');
      message.error('删除失败：订单ID为空，请刷新页面重试');
      setIsDeleteModalVisible(false);
      return;
    }

    console.log('用户点击弹窗「确认删除」，开始调用删除接口');
    console.log('当前要删除的订单ID：', finalOrderId, '类型：', typeof finalOrderId);
    
    try {
      setLoading(true);
      const deleteRes = await deleteOrder(finalOrderId); 
      console.log('删除订单接口返回结果：', deleteRes);
      
      if (deleteRes?.success) {
        message.success('订单删除成功');
        fetchOrders(searchText.trim(), pagination.current, pagination.pageSize);
      } else {
        console.error('删除订单接口返回失败：', deleteRes?.msg || '无错误信息');
        message.error(deleteRes?.msg || '删除订单失败');
      }
      setIsDeleteModalVisible(false);
      setCurrentDeleteOrderId('');
    } catch (err) {
      console.error('删除订单接口调用异常（完整信息）：', {
        status: err.response?.status,
        responseData: err.response?.data,
        errorMessage: err.message,
        requestConfig: err.config,
        requestUrl: err.config?.url
      });
      const errMsg = err.response?.data?.error || err.response?.data?.msg || `删除失败：${err.message}`;
      message.error(errMsg);
    } finally {
      setLoading(false);
      setCurrentDeleteOrderId('');
    }
  };

  // 打开批量删除弹窗
  const showBatchDeleteModal = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请至少选择一条订单记录');
      return;
    }
    setIsBatchDeleteModalVisible(true);
  };

  // 确认批量删除
  const handleBatchDeleteConfirm = async () => {
    console.log('开始批量删除，选中的ID列表：', selectedRowKeys);
    
    try {
      setLoading(true);
      const deleteRes = await batchDeleteOrders(selectedRowKeys);
      console.log('批量删除接口返回结果：', deleteRes);
      
      if (deleteRes?.success) {
        message.success(deleteRes.msg || '批量删除订单成功');
        fetchOrders(searchText.trim(), pagination.current, pagination.pageSize);
        setSelectedRowKeys([]); // 清空选中状态
      } else {
        console.error('批量删除接口返回失败：', deleteRes?.msg || '无错误信息');
        message.error(deleteRes?.msg || '批量删除订单失败');
      }
      setIsBatchDeleteModalVisible(false);
    } catch (err) {
      console.error('批量删除接口调用异常：', err);
      const errMsg = err.response?.data?.error || err.response?.data?.msg || `批量删除失败：${err.message}`;
      message.error(errMsg);
      setIsBatchDeleteModalVisible(false);
    } finally {
      setLoading(false);
    }
  };

  const openRechargeStatusModal = (record) => {
    const rs = Number(record?.recharge_status);
    const normalized = rs === 1 ? 1 : rs === 2 ? 2 : 0;
    setRechargeStatusModal({
      open: true,
      order: record,
      status: normalized,
    });
  };

  const submitRechargeStatusChange = async () => {
    const oid = rechargeStatusModal.order?.id;
    if (!oid) {
      message.error('订单信息缺失');
      return;
    }
    try {
      setLoading(true);
      const res = await updateAdminOrderRechargeResult(oid, {
        recharge_status: Number(rechargeStatusModal.status),
      });
      if (res?.success) {
        message.success(res.message || '充值到账状态已更新');
        setRechargeStatusModal({ open: false, order: null, status: 0 });
        fetchOrders(searchText.trim(), pagination.current, pagination.pageSize);
      } else {
        message.error(res?.error || res?.msg || '更新失败');
      }
    } catch (err) {
      const errMsg = err.response?.data?.error || err.response?.data?.msg || err.message || '更新失败';
      message.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  // 查看订单详情
  const handleViewDetail = async (orderId) => {
    try {
      setLoading(true);
      const res = await getOrderDetail(orderId);
      console.log('订单详情接口返回：', res);
      
      if (res?.success && res.data) {
        setOrderDetail(res.data);
        setIsDetailModalVisible(true);
      } else {
        message.error('获取订单详情失败');
      }
    } catch (err) {
      console.error('获取订单详情失败：', err);
      message.error('获取订单详情失败');
    } finally {
      setLoading(false);
    }
  };

  // 表格勾选配置
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => {
      console.log('选中的订单ID：', keys);
      setSelectedRowKeys(keys);
    },
  };

  // 原始表格列配置
  const originalColumns = [
    {
      title: '订单ID',
      dataIndex: 'id',
      key: 'id',
      width: 90,
    },
    {
      title: '订单号',
      dataIndex: 'order_no',
      key: 'order_no',
      width: 160,
    },
    {
      title: '用户ID',
      dataIndex: 'user_id',
      key: 'user_id',
      width: 80,
    },
    {
      title: '租客手机号',
      dataIndex: 'user_phone',
      key: 'user_phone',
      width: 120,
    },
    {
      title: '房源名称',
      dataIndex: 'house_name',
      key: 'house_name',
      width: 130,
      render: (v) => (v != null && String(v).trim() !== '' ? v : '-'),
    },
    {
      title: '房间号',
      dataIndex: 'room_fang_hao',
      key: 'room_fang_hao',
      width: 100,
      render: (v) => (v != null && String(v).trim() !== '' ? v : '-'),
    },
    {
      title: '房间ID',
      dataIndex: 'room_id',
      key: 'room_id',
      width: 100,
      render: (roomId) => {
        if (roomId === null || roomId === undefined) return '-';
        return roomId;
      },
    },
    {
      title: '订单类型',
      dataIndex: 'order_type',
      key: 'order_type',
      width: 120,
      render: (type) => {
        const typeMap = {
          1: '房租订单',
          2: '电表电费订单',
          3: '水表水费订单',
        };
        return typeMap[type] || '未知';
      },
    },
    {
      title: '订单金额',
      dataIndex: 'order_amount',
      key: 'order_amount',
      width: 100,
      render: (amount) => {
        const num = Number(amount);
        const finalAmount = isNaN(num) ? 0 : num;
        return `¥${finalAmount.toFixed(2)}`;
      },
    },
    {
      title: '电表服务费',
      key: 'electric_service_fee',
      width: 120,
      render: (_, record) => {
        const orderType = record.order_type;
        if (orderType === 2) {
          // 电表电费订单
          const orderAmount = Number(record.order_amount || 0);
          const landlordFee = Number(record.landlord_fee || 0);
          const serviceFee = orderAmount - landlordFee;
          const finalAmount = isNaN(serviceFee) ? 0 : serviceFee;
          return `¥${finalAmount.toFixed(2)}`;
        }
        return '¥0.00';
      },
    },
    {
      title: '水表服务费',
      key: 'water_service_fee',
      width: 120,
      render: (_, record) => {
        const orderType = record.order_type;
        if (orderType === 3) {
          // 水表水费订单
          const orderAmount = Number(record.order_amount || 0);
          const landlordFee = Number(record.landlord_fee || 0);
          const serviceFee = orderAmount - landlordFee;
          const finalAmount = isNaN(serviceFee) ? 0 : serviceFee;
          return `¥${finalAmount.toFixed(2)}`;
        }
        return '¥0.00';
      },
    },
    {
      title: '租房服务费',
      key: 'rent_service_fee',
      width: 120,
      render: (_, record) => {
        const orderType = record.order_type;
        if (orderType === 1) {
          // 房租订单
          const orderAmount = Number(record.order_amount || 0);
          const landlordFee = Number(record.landlord_fee || 0);
          const serviceFee = orderAmount - landlordFee;
          const finalAmount = isNaN(serviceFee) ? 0 : serviceFee;
          return `¥${finalAmount.toFixed(2)}`;
        }
        return '¥0.00';
      },
    },
    {
      title: '充值度数',
      dataIndex: 'recharge_power',
      key: 'recharge_power',
      width: 90,
      render: (power) => {
        if (power === null || power === undefined) return '-';
        return `${Number(power).toFixed(2)}度`;
      },
    },
    {
      title: '购电次数',
      key: 'meter_purchase_count',
      width: 92,
      render: (_, record) => {
        if (record.order_type !== 2) return '-';
        const v = record.meter_purchase_count ?? record.meterPurchaseCount;
        if (v === null || v === undefined || v === '') return '-';
        const n = Number(v);
        return Number.isFinite(n) ? String(Math.floor(n)) : '-';
      },
    },
    {
      title: '订单状态',
      dataIndex: 'order_status_text',
      key: 'order_status_text',
      width: 90,
      render: (text, record) => {
        const color = record.order_status === 1 ? 'green' : record.order_status === 2 ? 'red' : 'orange';
        const display = !text || text === '未知状态' || text === '未知' ? '支付取消' : text;
        return <Tag color={color}>{display}</Tag>;
      },
    },
    {
      title: '充值状态',
      dataIndex: 'recharge_status_text',
      key: 'recharge_status_text',
      width: 90,
      render: (text, record) => {
        // 核心修改1：房租订单（类型1）的充值状态显示为空
        if (record.order_type === 1) {
          return '-';
        }
        const rs = Number(record.recharge_status);
        const color = rs === 1 ? 'green' : rs === 2 ? 'processing' : 'red';
        return <Tag color={color}>{text || '未知'}</Tag>;
      },
    },
    {
      title: '支付时间',
      dataIndex: 'pay_time',
      key: 'pay_time',
      width: 150,
      render: (time) => {
        if (!time) return '-';
        return time;
      },
    },
    {
      title: '房东分账金额',
      dataIndex: 'landlord_fee',
      key: 'landlord_fee',
      width: 120,
      render: (amount) => {
        const num = Number(amount);
        const finalAmount = isNaN(num) ? 0 : num;
        return `¥${finalAmount.toFixed(2)}`;
      },
    },
    {
      title: '公司分账金额',
      dataIndex: 'company_fee',
      key: 'company_fee',
      width: 120,
      render: (amount) => {
        const num = Number(amount);
        const finalAmount = isNaN(num) ? 0 : num;
        return `¥${finalAmount.toFixed(2)}`;
      },
    },
    {
      title: '分账人A金额',
      dataIndex: 'shareholder_a_fee',
      key: 'shareholder_a_fee',
      width: 120,
      render: (amount) => {
        const num = Number(amount);
        const finalAmount = isNaN(num) ? 0 : num;
        return `¥${finalAmount.toFixed(2)}`;
      },
    },
    {
      title: '分账人B金额',
      dataIndex: 'shareholder_b_fee',
      key: 'shareholder_b_fee',
      width: 120,
      render: (amount) => {
        const num = Number(amount);
        const finalAmount = isNaN(num) ? 0 : num;
        return `¥${finalAmount.toFixed(2)}`;
      },
    },
    {
      title: '分账人C金额',
      dataIndex: 'shareholder_c_fee',
      key: 'shareholder_c_fee',
      width: 120,
      render: (amount) => {
        const num = Number(amount);
        const finalAmount = isNaN(num) ? 0 : num;
        return `¥${finalAmount.toFixed(2)}`;
      },
    },
    {
      title: '银行手续费',
      dataIndex: 'bank_fee',
      key: 'bank_fee',
      width: 120,
      render: (amount) => {
        const num = Number(amount);
        const finalAmount = isNaN(num) ? 0 : num;
        return `¥${finalAmount.toFixed(2)}`;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'create_time',
      key: 'create_time',
      width: 150,
    },
    {
      title: '操作',
      key: 'action',
      width: 280,
      render: (_, record) => {
        const canEditRecharge =
          (record.order_type === 2 || record.order_type === 3) && Number(record.order_status) === 1;
        return (
          <Space size="small" wrap>
            <Button 
              size="small" 
              type="primary" 
              icon={<EyeOutlined />}
              onClick={() => handleViewDetail(record.id)}
            >
              查看详情
            </Button>
            {canEditRecharge ? (
              <Button size="small" onClick={() => openRechargeStatusModal(record)}>
                改到账状态
              </Button>
            ) : null}
            <Button 
              size="small" 
              type="danger" 
              icon={<DeleteOutlined />}
              onClick={() => showDeleteModal(record.id)}
            >
              删除
            </Button>
          </Space>
        );
      },
    },
  ];

  // 从localStorage加载列设置并应用到表格（只处理隐藏）
  useEffect(() => {
    const storageKey = 'orderList_columnStates';
    const saved = localStorage.getItem(storageKey);
    let columnStates = {};
    
    if (saved) {
      try {
        columnStates = JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse column states:', e);
      }
    }
    
    // 根据列状态生成实际列配置（只处理隐藏）
    const processedColumns = originalColumns.map(col => {
      const state = columnStates[col.key];
      
      if (state === 'hidden') {
        return null;
      }
      
      return col;
    }).filter(col => col !== null);
    
    setDisplayColumns(processedColumns);
  }, []);

  return (
    <div style={{ padding: '20px 0' }}>
      <PageTitle title="订单管理" />
      <Card bordered={false} style={{ marginTop: 0 }}>
      <ListToolbar
        searchPlaceholder="搜索订单ID/订单号/用户ID/手机号/表号"
        searchValue={searchText}
        onSearchChange={setSearchText}
        onSearch={handleSearch}
        onReset={handleReset}
        onRefresh={handleRefresh}
        onExport={handleExport}
        showBatchDelete={true}
        selectedCount={selectedRowKeys.length}
        onBatchDelete={showBatchDeleteModal}
      />

      <ResizableTable
        columns={displayColumns.length > 0 ? displayColumns : originalColumns}
        dataSource={orderData}
        loading={loading}
        storageKey="orderList"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条订单`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        onChange={handleTableChange}
        scroll={{ x: 2380 }}
        rowKey="id"
        rowSelection={rowSelection}
      />

      {/* 单个删除确认弹窗 */}
      <Modal
        title="确认删除订单"
        open={isDeleteModalVisible}
        onCancel={() => {
          console.log('用户点击弹窗「取消」或遮罩，关闭删除弹窗');
          setIsDeleteModalVisible(false);
          setCurrentDeleteOrderId('');
        }}
        onOk={handleDeleteConfirm}
        width={500}
        destroyOnClose
        maskClosable={false}
        okText="确认删除"
        cancelText="取消"
      >
        <div style={{ lineHeight: 1.8 }}>
          <p>确定要删除订单ID为 <strong>{currentDeleteOrderId}</strong> 的订单吗？</p>
          <p style={{ color: '#f50' }}>⚠️ 此操作不可恢复，请谨慎操作！</p>
        </div>
      </Modal>

      {/* 批量删除确认弹窗 */}
      <Modal
        title="确认批量删除订单"
        open={isBatchDeleteModalVisible}
        onCancel={() => setIsBatchDeleteModalVisible(false)}
        onOk={handleBatchDeleteConfirm}
        width={500}
        destroyOnClose
        maskClosable={false}
        okText="确认删除"
        cancelText="取消"
      >
        <div style={{ lineHeight: 1.8 }}>
          <p>确定要删除选中的 <strong>{selectedRowKeys.length}</strong> 条订单吗？</p>
          <p style={{ color: '#f50' }}>⚠️ 此操作不可恢复，请谨慎操作！</p>
          <p>选中的订单ID：{selectedRowKeys.join(', ')}</p>
        </div>
      </Modal>

      {/* 管理端：手动改充值到账状态（调 POST /api/admin/orders/:id/recharge-result） */}
      <Modal
        title="修改充值到账状态（测试）"
        open={rechargeStatusModal.open}
        onCancel={() => setRechargeStatusModal({ open: false, order: null, status: 0 })}
        onOk={submitRechargeStatusChange}
        okText="保存"
        cancelText="取消"
        destroyOnClose
        maskClosable={false}
        width={520}
      >
        {rechargeStatusModal.order ? (
          <div style={{ lineHeight: 2 }}>
            <Typography.Paragraph type="warning" style={{ marginBottom: 12 }}>
              将直接写入数据库订单的 <code>recharge_status</code>，用于联调/测试。请谨慎操作。
            </Typography.Paragraph>
            <p><strong>订单 ID：</strong>{rechargeStatusModal.order.id}</p>
            <p><strong>类型：</strong>{rechargeStatusModal.order.order_type === 2 ? '电费' : '水费'}</p>
            <p><strong>当前状态：</strong>{rechargeStatusModal.order.recharge_status_text || '-'}</p>
            <div style={{ marginTop: 12 }}>
              <strong>目标状态：</strong>
              <Select
                style={{ width: '100%', marginTop: 8 }}
                value={rechargeStatusModal.status}
                onChange={(v) => setRechargeStatusModal((prev) => ({ ...prev, status: v }))}
                options={[
                  { value: 0, label: '0 — 充值异常（小程序可显示「补电量」等）' },
                  { value: 1, label: '1 — 充值成功（已到账）' },
                  { value: 2, label: '2 — 处理中（如 ZT 水远程）' },
                ]}
              />
            </div>
          </div>
        ) : null}
      </Modal>

      {/* 订单详情弹窗 */}
      <Modal
        title="订单详情"
        open={isDetailModalVisible}
        onCancel={() => {
          setIsDetailModalVisible(false);
          setOrderDetail(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setIsDetailModalVisible(false);
            setOrderDetail(null);
          }}>
            关闭
          </Button>
        ]}
        width={800}
        destroyOnClose
      >
        {orderDetail && (
          <div style={{ lineHeight: 2 }}>
            <Row gutter={[16, 8]}>
              <Col span={12}><strong>订单ID：</strong>{orderDetail.id}</Col>
              <Col span={12}><strong>订单号：</strong>{orderDetail.order_no || '-'}</Col>
              <Col span={12}><strong>用户ID：</strong>{orderDetail.user_id}</Col>
              <Col span={12}><strong>租客手机号：</strong>{orderDetail.user_phone || '-'}</Col>
              <Col span={12}><strong>房源名称：</strong>{orderDetail.house_name && String(orderDetail.house_name).trim() !== '' ? orderDetail.house_name : '-'}</Col>
              <Col span={12}><strong>房间号：</strong>{orderDetail.room_fang_hao && String(orderDetail.room_fang_hao).trim() !== '' ? orderDetail.room_fang_hao : '-'}</Col>
              <Col span={12}><strong>房间ID：</strong>{orderDetail.room_id ?? '-'}</Col>
              <Col span={12}><strong>设备ID：</strong>{orderDetail.device_id || '-'}</Col>
              <Col span={12}><strong>表号：</strong>{orderDetail.meter_no || '-'}</Col>
              <Col span={12}><strong>订单金额：</strong>¥{Number(orderDetail.order_amount || 0).toFixed(2)}</Col>
              <Col span={12}><strong>订单类型：</strong>{
                (() => {
                  const typeMap = {
                    1: '房租订单',
                    2: '电表电费订单',
                    3: '水表水费订单',
                  };
                  return typeMap[orderDetail.order_type] || '未知';
                })()
              }</Col>
              <Col span={12}><strong>电表服务费：</strong>{
                orderDetail.order_type === 2 
                  ? `¥${(() => {
                      const orderAmount = Number(orderDetail.order_amount || 0);
                      const landlordFee = Number(orderDetail.landlord_fee || 0);
                      const serviceFee = orderAmount - landlordFee;
                      return isNaN(serviceFee) ? 0 : serviceFee;
                    })().toFixed(2)}`
                  : '¥0.00'
              }</Col>
              <Col span={12}><strong>水表服务费：</strong>{
                orderDetail.order_type === 3 
                  ? `¥${(() => {
                      const orderAmount = Number(orderDetail.order_amount || 0);
                      const landlordFee = Number(orderDetail.landlord_fee || 0);
                      const serviceFee = orderAmount - landlordFee;
                      return isNaN(serviceFee) ? 0 : serviceFee;
                    })().toFixed(2)}`
                  : '¥0.00'
              }</Col>
              <Col span={12}><strong>租房服务费：</strong>{
                orderDetail.order_type === 1 
                  ? `¥${(() => {
                      const orderAmount = Number(orderDetail.order_amount || 0);
                      const landlordFee = Number(orderDetail.landlord_fee || 0);
                      const serviceFee = orderAmount - landlordFee;
                      return isNaN(serviceFee) ? 0 : serviceFee;
                    })().toFixed(2)}`
                  : '¥0.00'
              }</Col>
              <Col span={12}><strong>购电次数：</strong>{
                orderDetail.order_type === 2
                  ? (() => {
                      const v = orderDetail.meter_purchase_count ?? orderDetail.meterPurchaseCount;
                      if (v === null || v === undefined || v === '') return '-';
                      const n = Number(v);
                      return Number.isFinite(n) ? String(Math.floor(n)) : '-';
                    })()
                  : '-'
              }（电表订单快照，与小程序读表比对）</Col>
              <Col span={12}><strong>订单状态：</strong>
                <Tag color={orderDetail.order_status === 1 ? 'green' : orderDetail.order_status === 2 ? 'red' : 'orange'}>
                  {(!orderDetail.order_status_text || orderDetail.order_status_text === '未知状态' || orderDetail.order_status_text === '未知') ? '支付取消' : orderDetail.order_status_text}
                </Tag>
              </Col>
              <Col span={12}><strong>充值度数：</strong>{orderDetail.recharge_power ? `${Number(orderDetail.recharge_power).toFixed(2)}度` : '-'}</Col>
              {/* 详情弹窗中也按规则处理：房租订单充值状态为空 */}
              <Col span={12}><strong>充值状态：</strong>
                {orderDetail.order_type === 1 ? '-' : (
                  <Tag color={Number(orderDetail.recharge_status) === 1 ? 'green' : Number(orderDetail.recharge_status) === 2 ? 'processing' : 'red'}>
                    {orderDetail.recharge_status_text || '未知'}
                  </Tag>
                )}
              </Col>
              <Col span={12}><strong>总电价：</strong>{orderDetail.total_price ? `¥${Number(orderDetail.total_price).toFixed(2)}/度` : '-'}</Col>
              <Col span={12}><strong>电价：</strong>{orderDetail.unit_price ? `¥${Number(orderDetail.unit_price).toFixed(2)}/度` : '-'}</Col>
              <Col span={12}><strong>管理费：</strong>{orderDetail.management_fee ? `¥${Number(orderDetail.management_fee).toFixed(2)}/度` : '-'}</Col>
              <Col span={12}><strong>服务费：</strong>{orderDetail.service_fee ? `¥${Number(orderDetail.service_fee).toFixed(2)}/度` : '-'}</Col>
              <Col span={12}><strong>充值前电量：</strong>{orderDetail.before_power ? `${Number(orderDetail.before_power).toFixed(2)}度` : '-'}</Col>
              <Col span={12}><strong>充值后电量：</strong>{orderDetail.after_power ? `${Number(orderDetail.after_power).toFixed(2)}度` : '-'}</Col>
              <Col span={12}><strong>实际增加电量：</strong>{orderDetail.actual_recharge_power ? `${Number(orderDetail.actual_recharge_power).toFixed(2)}度` : '-'}</Col>
              <Col span={12}><strong>支付时间：</strong>{orderDetail.pay_time || '-'}</Col>
              
              {/* 分账信息区域 */}
              <Col span={24} style={{ marginTop: 16, marginBottom: 8 }}>
                <strong style={{ fontSize: '14px', color: '#1890ff' }}>分账信息（根据订单类型使用对应的分账比例）</strong>
              </Col>
              <Col span={12}><strong>房东分账金额：</strong>¥{Number(orderDetail.landlord_fee || 0).toFixed(2)}</Col>
              <Col span={12}><strong>公司分账金额：</strong>¥{Number(orderDetail.company_fee || 0).toFixed(2)}</Col>
              <Col span={12}><strong>分账人A金额：</strong>¥{Number(orderDetail.shareholder_a_fee || 0).toFixed(2)}</Col>
              <Col span={12}><strong>分账人B金额：</strong>¥{Number(orderDetail.shareholder_b_fee || 0).toFixed(2)}</Col>
              <Col span={12}><strong>分账人C金额：</strong>¥{Number(orderDetail.shareholder_c_fee || 0).toFixed(2)}</Col>
              <Col span={12}><strong>银行手续费：</strong>¥{Number(orderDetail.bank_fee || 0).toFixed(2)}</Col>
              <Col span={24} style={{ marginTop: 8, marginBottom: 8 }}>
                <span style={{ fontSize: '12px', color: '#999' }}>
                  {(() => {
                    const typeMap = {
                      1: '租房服务费分账比例',
                      2: '电表服务费分账比例',
                      3: '水表服务费分账比例',
                    };
                    return `注：本订单使用${typeMap[orderDetail.order_type] || '未知'}进行分账，可在"分账管理"中查看和修改分账比例配置。`;
                  })()}
                </span>
              </Col>
              
              <Col span={12}><strong>创建时间：</strong>{orderDetail.create_time}</Col>
              <Col span={12}><strong>更新时间：</strong>{orderDetail.update_time}</Col>
            </Row>
          </div>
        )}
      </Modal>
      </Card>
    </div>
  );
};

export default OrderList;